import { TriangleAlert } from "lucide-react";
import { useMemo } from "react";

import { AdminConsentBadge, RiskBadge } from "@/components/app-registration-badges";
import { Badge } from "@/components/ui/badge";
import { useAppRegistrationScopes } from "@/lib/api";
import {
  highestRisk,
  indexScopes,
  needsAdminConsent,
  unrecognisedKeys,
} from "@/lib/app-registration-scopes";
import { cn } from "@/lib/utils";

/**
 * The one-line verdict on a request's permissions, for a collapsed table row:
 * how many, how bad the worst one is, and whether it needs a directory admin.
 *
 * Every row calls the catalogue hook rather than being handed the data —
 * TanStack Query dedupes them into one request, and an hour-long `staleTime`
 * means a table of thirty rows still fetches it once.
 */
export function ScopeSummary({ scopeKeys }: { scopeKeys: string[] }) {
  const { data: scopes, isLoading, error } = useAppRegistrationScopes();
  const catalogue = useMemo(() => indexScopes(scopes), [scopes]);

  if (scopeKeys.length === 0) {
    return <span className="text-muted-foreground text-xs">No Graph permissions</span>;
  }

  const count = (
    <span className="text-foreground text-xs font-medium">
      {scopeKeys.length} permission{scopeKeys.length === 1 ? "" : "s"}
    </span>
  );

  // Without the catalogue there is no risk tier to show. Say so once, rather
  // than flagging every key as unrecognised — that reads as a bad request
  // when it is really a failed fetch.
  if (isLoading || error) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {count}
        {error && (
          <Badge variant="warning">
            <TriangleAlert />
            Risk unavailable
          </Badge>
        )}
      </div>
    );
  }

  const risk = highestRisk(scopeKeys, catalogue);
  const unrecognised = unrecognisedKeys(scopeKeys, catalogue);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {count}
      {risk && <RiskBadge risk={risk} />}
      {needsAdminConsent(scopeKeys, catalogue) && <AdminConsentBadge />}
      {unrecognised.length > 0 && (
        <Badge variant="warning">
          <TriangleAlert />
          {unrecognised.length} unrecognised
        </Badge>
      )}
    </div>
  );
}

interface ScopeListProps {
  scopeKeys: string[];
  /** Hide the plain-language description — used where space is tight. */
  compact?: boolean;
  className?: string;
}

/**
 * Every permission the request asks for, each with its risk tier and whether a
 * directory admin has to consent to it.
 *
 * An approver who cannot see this is approving blind, so a key the catalogue
 * does not know is rendered as a warning rather than skipped: it means this
 * request carries a permission the server's catalogue no longer lists, not
 * that the permission is absent from the request.
 */
export function ScopeList({ scopeKeys, compact = false, className }: ScopeListProps) {
  const { data: scopes, isLoading, error } = useAppRegistrationScopes();
  const catalogue = useMemo(() => indexScopes(scopes), [scopes]);

  if (scopeKeys.length === 0) {
    return (
      <p className={cn("text-muted-foreground text-sm", className)}>
        No Microsoft Graph permissions requested &mdash; the app registration is for sign-in or a
        credential only.
      </p>
    );
  }

  // Still fetching: show the raw keys so the request is never blank, but don't
  // dress them up as unrecognised — nothing has been resolved yet.
  if (isLoading) {
    return (
      <ul className={cn("space-y-2", className)}>
        {scopeKeys.map((key) => (
          <li key={key} className="border-border rounded-md border p-2.5">
            <code className="text-foreground text-xs font-medium">{key}</code>
            <p className="text-muted-foreground mt-1 text-xs">Loading permission details…</p>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      {error && (
        <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Couldn&rsquo;t load the permission catalogue, so the risk and consent type of each key
            below is unknown. Reload before deciding.
          </span>
        </p>
      )}
      <ul className="space-y-2">
        {scopeKeys.map((key) => {
          const scope = catalogue.get(key);
          if (!scope) {
            return (
              <li
                key={key}
                className="rounded-md border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-800 dark:bg-amber-950/40"
              >
                <div className="flex items-center gap-2">
                  <TriangleAlert className="size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <code className="text-foreground text-xs">{key}</code>
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Unrecognised permission &mdash; not in the server&rsquo;s catalogue. Check its
                  risk and consent type before approving.
                </p>
              </li>
            );
          }
          return (
            <li key={key} className="border-border rounded-md border p-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-foreground text-xs font-medium">{scope.value}</code>
                <Badge variant="ghost" className="text-muted-foreground border-border">
                  {scope.group}
                </Badge>
                <RiskBadge risk={scope.risk} />
                {scope.admin_consent_required && <AdminConsentBadge />}
              </div>
              {!compact && (
                <p className="text-muted-foreground mt-1 text-xs">{scope.description}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
